import { ApiError } from "api/client";
import { submitSupportTicket, type SupportTicketPayload } from "api/support";
import { HoneypotField } from "components";
import React, { type ChangeEvent, type FormEvent, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSession } from "Session";

import type { SupportFormStatus } from "./helpSupport.types";

import styles from "./SupportForm.module.css";

/** Shortest message the backend accepts; checked here so the reason is shown before the round trip. */
const MIN_MESSAGE_LENGTH = 20;

const SupportForm = () => {
	const { t } = useTranslation(["helpSupportPage", "common"]);
	const { hasSession, getAccessToken } = useSession();

	// Name/email are collected from anonymous visitors only; signed-in submitters are identified
	// by their token, so the fields are hidden and the backend uses the account's details.
	const [name, setName] = useState("");
	const [email, setEmail] = useState("");
	const [subject, setSubject] = useState("");
	const [message, setMessage] = useState("");
	// Honeypot: hidden from real users, so a value here means a bot filled the form in.
	const [website, setWebsite] = useState("");
	const [status, setStatus] = useState<SupportFormStatus>("idle");
	const [feedback, setFeedback] = useState("");

	const onNameChange = useCallback((event: ChangeEvent<HTMLInputElement>): void => {
		setName(event.target.value);
	}, []);

	const onEmailChange = useCallback((event: ChangeEvent<HTMLInputElement>): void => {
		setEmail(event.target.value);
	}, []);

	const onSubjectChange = useCallback((event: ChangeEvent<HTMLInputElement>): void => {
		setSubject(event.target.value);
	}, []);

	const onMessageChange = useCallback((event: ChangeEvent<HTMLTextAreaElement>): void => {
		setMessage(event.target.value);
	}, []);

	const onWebsiteChange = useCallback((event: ChangeEvent<HTMLInputElement>): void => {
		setWebsite(event.target.value);
	}, []);

	const onSubmit = useCallback(
		async (event: FormEvent<HTMLFormElement>): Promise<void> => {
			event.preventDefault();

			if (status === "submitting") {
				return;
			}

			const payload: SupportTicketPayload = {
				subject: subject.trim(),
				message: message.trim()
			};

			if (!hasSession) {
				payload.name = name.trim();
				payload.email = email.trim();
				if (!payload.name || !payload.email) {
					return;
				}
			}

			if (!payload.subject || !payload.message) {
				return;
			}

			if (payload.message.length < MIN_MESSAGE_LENGTH) {
				setStatus("error");
				setFeedback(t("helpSupportPage:form.messageTooShort", { min: MIN_MESSAGE_LENGTH }));

				return;
			}

			// Only sent when a bot has filled the hidden field; the backend discards those submissions.
			if (website) {
				payload.website = website;
			}

			setStatus("submitting");
			setFeedback("");

			try {
				const token = hasSession ? await getAccessToken() : undefined;
				await submitSupportTicket(payload, token);
				setStatus("success");
				setFeedback(t("helpSupportPage:form.success"));
				setSubject("");
				setMessage("");
			} catch (error) {
				setStatus("error");
				setFeedback(error instanceof ApiError ? error.message : t("common:genericError"));
			}
		},
		[email, getAccessToken, hasSession, message, name, status, subject, t, website]
	);

	return (
		<form className={styles.form} onSubmit={onSubmit}>
			{hasSession ? (
				<p className={styles.signedInNote}>{t("helpSupportPage:form.signedInNote")}</p>
			) : (
				<>
					<label className={styles.field} htmlFor="support-name">
						<span>{t("helpSupportPage:form.nameLabel")}</span>
						<input
							disabled={status === "submitting"}
							id="support-name"
							maxLength={100}
							onChange={onNameChange}
							placeholder={t("helpSupportPage:form.namePlaceholder")}
							type="text"
							value={name}
							required
						/>
					</label>

					<label className={styles.field} htmlFor="support-email">
						<span>{t("helpSupportPage:form.emailLabel")}</span>
						<input
							disabled={status === "submitting"}
							id="support-email"
							maxLength={256}
							onChange={onEmailChange}
							placeholder={t("helpSupportPage:form.emailPlaceholder")}
							type="email"
							value={email}
							required
						/>
					</label>
				</>
			)}

			<label className={styles.field} htmlFor="support-subject">
				<span>{t("helpSupportPage:form.subjectLabel")}</span>
				<input
					disabled={status === "submitting"}
					id="support-subject"
					maxLength={200}
					onChange={onSubjectChange}
					placeholder={t("helpSupportPage:form.subjectPlaceholder")}
					type="text"
					value={subject}
					required
				/>
			</label>

			<label className={styles.field} htmlFor="support-message">
				<span>{t("helpSupportPage:form.messageLabel")}</span>
				<textarea
					disabled={status === "submitting"}
					id="support-message"
					maxLength={4000}
					onChange={onMessageChange}
					placeholder={t("helpSupportPage:form.messagePlaceholder")}
					rows={6}
					value={message}
					required
				/>
			</label>

			<HoneypotField id="support-website" onChange={onWebsiteChange} value={website} />

			<button className={styles.submit} disabled={status === "submitting"} type="submit">
				{status === "submitting" ? t("helpSupportPage:form.submitting") : t("helpSupportPage:form.submit")}
			</button>

			{feedback ? (
				<p className={status === "error" ? styles.error : styles.success} role="status">
					{feedback}
				</p>
			) : null}
		</form>
	);
};

export default SupportForm;
